import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx'; // Asegúrate de instalar la librería: npm install xlsx
import './FluidezLectora.css';

const levelOptions = [
  '1A','1B','2A','2B','3A','3B','4A','4B','5A','5B','6A','6B','7A','7B','8A','8B','IA','IB','IIA','IIB','IIIA','IIIB','IVA','IVB'
];

const gradeFields = [
  'math_eval1', 'math_eval1a',
  'math_eval2', 'math_eval2a',
  'math_eval3', 'math_eval3a',
  'math_eval4', 'math_eval4a'
];

const lowLevels = ['1A', '1B', '2A', '2B', '3A', '3B'];

const FluidezMatematicas = () => {
  const [filterYear, setFilterYear] = useState('');
  const [filterNivel, setFilterNivel] = useState('');
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');
  
  // Referencia para el input de archivo CSV (oculto)
  const fileInputRef = useRef(null);

  const safeTrim = (value) => {
    return value === null || value === undefined ? '' : String(value).trim();
  };

  const fetchStudents = async () => {
    if (!filterYear || !filterNivel) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `https://asistencia-betterland.onrender.com/fluidez/estudiantes?yearFluidez=${filterYear}&nivel=${filterNivel}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        data.sort((a, b) => {
          if (a.lastnamepaternal < b.lastnamepaternal) return -1;
          if (a.lastnamepaternal > b.lastnamepaternal) return 1;
          return 0;
        });
        const modifiedData = data.map(student => ({
          ...student,
          math_eval1: student.math_eval1 === null ? '' : student.math_eval1,
          math_eval1a: student.math_eval1a === null ? '' : student.math_eval1a,
          math_eval2: student.math_eval2 === null ? '' : student.math_eval2,
          math_eval2a: student.math_eval2a === null ? '' : student.math_eval2a,
          math_eval3: student.math_eval3 === null ? '' : student.math_eval3,
          math_eval3a: student.math_eval3a === null ? '' : student.math_eval3a,
          math_eval4: student.math_eval4 === null ? '' : student.math_eval4,
          math_eval4a: student.math_eval4a === null ? '' : student.math_eval4a
        }));
        console.log("Estudiantes recibidos:", modifiedData);
        setStudents(modifiedData);
      } else {
        setStudents([]);
      }
    } catch (error) {
      console.error("Error al cargar estudiantes:", error);
    } finally {
      setLoading(false);
    }
  };

  // Determina si el nivel seleccionado es de niveles bajos
  const isLowLevel = lowLevels.includes(filterNivel);
  // Define los campos y etiquetas según el nivel
  const fieldsToRender = isLowLevel
    ? ['math_eval1', 'math_eval2', 'math_eval3', 'math_eval4']
    : gradeFields;
  const headerLabels = isLowLevel
    ? ['Nota 1', 'Nota 2', 'Nota 3', 'Nota 4']
    : ['Nota 1 +', 'Nota 1 x', 'Nota 2 +', 'Nota 2 x', 'Nota 3 +', 'Nota 3 x', 'Nota 4 +', 'Nota 4 x'];

  const handleInputChange = (e, index, field) => {
    const newStudents = [...students];
    newStudents[index][field] = e.target.value;
    setStudents(newStudents);
  };

  // Maneja la navegación con flechas en los inputs
  const handleKeyDown = (e, rowIndex, field, fieldsArray = gradeFields) => {
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    } else {
      return;
    }
    const currentFieldIndex = fieldsArray.indexOf(field);
    let targetRow = rowIndex;
    let targetFieldIndex = currentFieldIndex;
    switch(e.key) {
      case 'ArrowRight':
        if (currentFieldIndex < fieldsArray.length - 1) {
          targetFieldIndex = currentFieldIndex + 1;
        }
        break;
      case 'ArrowLeft':
        if (currentFieldIndex > 0) {
          targetFieldIndex = currentFieldIndex - 1;
        }
        break;
      case 'ArrowUp':
        if (rowIndex > 0) {
          targetRow = rowIndex - 1;
        }
        break;
      case 'ArrowDown':
        if (rowIndex < students.length - 1) {
          targetRow = rowIndex + 1;
        }
        break;
      default:
        break;
    }
    const targetField = fieldsArray[targetFieldIndex];
    const targetInput = document.getElementById(`input-${targetRow}-${targetField}`);
    if (targetInput) {
      targetInput.focus();
    }
  };

  // Función auxiliar para obtener las evaluaciones según el nivel
  const getEvaluations = (student) => {
    if (isLowLevel) {
      return {
        math_eval1: safeTrim(student.math_eval1) === '' ? null : Number(safeTrim(student.math_eval1)),
        math_eval2: safeTrim(student.math_eval2) === '' ? null : Number(safeTrim(student.math_eval2)),
        math_eval3: safeTrim(student.math_eval3) === '' ? null : Number(safeTrim(student.math_eval3)),
        math_eval4: safeTrim(student.math_eval4) === '' ? null : Number(safeTrim(student.math_eval4))
      };
    } else {
      return {
        math_eval1: safeTrim(student.math_eval1) === '' ? null : Number(safeTrim(student.math_eval1)),
        math_eval1a: safeTrim(student.math_eval1a) === '' ? null : Number(safeTrim(student.math_eval1a)),
        math_eval2: safeTrim(student.math_eval2) === '' ? null : Number(safeTrim(student.math_eval2)),
        math_eval2a: safeTrim(student.math_eval2a) === '' ? null : Number(safeTrim(student.math_eval2a)),
        math_eval3: safeTrim(student.math_eval3) === '' ? null : Number(safeTrim(student.math_eval3)),
        math_eval3a: safeTrim(student.math_eval3a) === '' ? null : Number(safeTrim(student.math_eval3a)),
        math_eval4: safeTrim(student.math_eval4) === '' ? null : Number(safeTrim(student.math_eval4)),
        math_eval4a: safeTrim(student.math_eval4a) === '' ? null : Number(safeTrim(student.math_eval4a))
      };
    }
  };

  const handleSave = async (student) => {
    try {
      const token = localStorage.getItem('token');
      const evaluations = getEvaluations(student);
      const response = await fetch(
        `https://asistencia-betterland.onrender.com/fluidez/estudiantes/${student.rut}/evaluaciones/math`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            yearFluidez: filterYear,
            ...evaluations
          })
        }
      );
      if (response.ok) {
        setUpdateMessage(`Evaluaciones actualizadas para ${student.firstname} ${student.lastnamepaternal}`);
        fetchStudents();
      } else {
        setUpdateMessage("Error al actualizar evaluaciones.");
      }
    } catch (error) {
      console.error("Error al actualizar:", error);
      setUpdateMessage("Error al conectar con el servidor.");
    }
  };

  const handleSaveAll = async () => {
    try {
      const token = localStorage.getItem('token');
      const promises = students.map(student => {
        const evaluations = getEvaluations(student);
        return fetch(
          `https://asistencia-betterland.onrender.com/fluidez/estudiantes/${student.rut}/evaluaciones/math`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              yearFluidez: filterYear,
              ...evaluations
            })
          }
        );
      });
      const results = await Promise.all(promises);
      const allOk = results.every(res => res.ok);
      if (allOk) {
        setUpdateMessage("Evaluaciones actualizadas para todos los estudiantes.");
        fetchStudents();
      } else {
        setUpdateMessage("Error al actualizar algunas evaluaciones.");
      }
    } catch (error) {
      console.error("Error al actualizar en lote:", error);
      setUpdateMessage("Error al conectar con el servidor para guardar todo.");
    }
  };

  // Exporta el CSV del nivel actual (ya existente)
  const exportCSV = () => {
    if (students.length === 0) return;
    const baseHeaders = [
      "Nivel",
      "RUT",
      "Apellido Paterno",
      "Apellido Materno",
      "Nombres"
    ];
    const evalHeaders = isLowLevel
      ? ["Fluidez Matemáticas 1", "Fluidez Matemáticas 2", "Fluidez Matemáticas 3", "Fluidez Matemáticas 4"]
      : ["Fluidez Matemáticas 1", "Fluidez Matemáticas 1a", "Fluidez Matemáticas 2", "Fluidez Matemáticas 2a", "Fluidez Matemáticas 3", "Fluidez Matemáticas 3a", "Fluidez Matemáticas 4", "Fluidez Matemáticas 4a"];
    const headers = [...baseHeaders, ...evalHeaders];
    const rows = students.map(stu => {
      const baseData = [stu.nivel, stu.rut, stu.lastnamepaternal, stu.lastnamematernal, stu.firstname];
      const evalData = isLowLevel
        ? [stu.math_eval1, stu.math_eval2, stu.math_eval3, stu.math_eval4]
        : [stu.math_eval1, stu.math_eval1a, stu.math_eval2, stu.math_eval2a, stu.math_eval3, stu.math_eval3a, stu.math_eval4, stu.math_eval4a];
      return [...baseData, ...evalData];
    });
    const csvContent =
      "\uFEFF" + headers.join(";") + "\n" + rows.map(row => row.join(";")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `fluidez_matematicas_${filterYear}_${filterNivel}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Función para importar CSV (ajustada para niveles bajos)
  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const cleanText = text.replace(/^\uFEFF/, '');
      const lines = cleanText.split("\n").filter(line => line.trim() !== "");
      if (lines.length === 0) return;
      const headers = lines[0].split(";").map(h => h.trim());
      const data = lines.slice(1).map(line => {
        const values = line.split(";").map(v => v.trim());
        const obj = {};
        headers.forEach((header, i) => {
          obj[header] = values[i];
        });
        return obj;
      });
      // Mapeamos los datos al formato de estudiante según el nivel
      const importedStudents = data.map(item => {
        const base = {
          nivel: item["Nivel"],
          rut: item["RUT"],
          lastnamepaternal: item["Apellido Paterno"],
          lastnamematernal: item["Apellido Materno"],
          firstname: item["Nombres"]
        };
        if (lowLevels.includes(item["Nivel"])) {
          return {
            ...base,
            math_eval1: item["Fluidez Matemáticas 1"],
            math_eval2: item["Fluidez Matemáticas 2"],
            math_eval3: item["Fluidez Matemáticas 3"],
            math_eval4: item["Fluidez Matemáticas 4"],
            math_eval1a: '',
            math_eval2a: '',
            math_eval3a: '',
            math_eval4a: ''
          };
        } else {
          return {
            ...base,
            math_eval1: item["Fluidez Matemáticas 1"],
            math_eval1a: item["Fluidez Matemáticas 1a"],
            math_eval2: item["Fluidez Matemáticas 2"],
            math_eval2a: item["Fluidez Matemáticas 2a"],
            math_eval3: item["Fluidez Matemáticas 3"],
            math_eval3a: item["Fluidez Matemáticas 3a"],
            math_eval4: item["Fluidez Matemáticas 4"],
            math_eval4a: item["Fluidez Matemáticas 4a"]
          };
        }
      });
      setStudents(importedStudents);
    };
    reader.readAsText(file, "UTF-8");
  };

  // --- NUEVA FUNCIONALIDAD PARA EXPORTAR TODOS LOS NIVELES A EXCEL CON PESTAÑAS ORDENADAS ---

  // Función auxiliar para obtener los datos de un nivel
  const fetchDataForLevel = async (level) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(
        `https://asistencia-betterland.onrender.com/fluidez/estudiantes?yearFluidez=${filterYear}&nivel=${level}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        data.sort((a, b) => {
          if (a.lastnamepaternal < b.lastnamepaternal) return -1;
          if (a.lastnamepaternal > b.lastnamepaternal) return 1;
          return 0;
        });
        // Normalizamos los campos nulos
        const modifiedData = data.map(student => ({
          ...student,
          math_eval1: student.math_eval1 === null ? '' : student.math_eval1,
          math_eval1a: student.math_eval1a === null ? '' : student.math_eval1a,
          math_eval2: student.math_eval2 === null ? '' : student.math_eval2,
          math_eval2a: student.math_eval2a === null ? '' : student.math_eval2a,
          math_eval3: student.math_eval3 === null ? '' : student.math_eval3,
          math_eval3a: student.math_eval3a === null ? '' : student.math_eval3a,
          math_eval4: student.math_eval4 === null ? '' : student.math_eval4,
          math_eval4a: student.math_eval4a === null ? '' : student.math_eval4a
        }));
        return modifiedData;
      } else {
        return [];
      }
    } catch (error) {
      console.error(`Error al cargar datos para el nivel ${level}:`, error);
      return [];
    }
  };

  // Función auxiliar para armar los datos (cabeceras y filas) para cada hoja
  const createSheetData = (level, studentsData) => {
    const isLow = lowLevels.includes(level);
    const baseHeaders = ["Nivel", "RUT", "Apellido Paterno", "Apellido Materno", "Nombres"];
    const evalHeaders = isLow
      ? ["Fluidez Matemáticas 1", "Fluidez Matemáticas 2", "Fluidez Matemáticas 3", "Fluidez Matemáticas 4"]
      : ["Fluidez Matemáticas 1", "Fluidez Matemáticas 1a", "Fluidez Matemáticas 2", "Fluidez Matemáticas 2a", "Fluidez Matemáticas 3", "Fluidez Matemáticas 3a", "Fluidez Matemáticas 4", "Fluidez Matemáticas 4a"];
    const headers = [...baseHeaders, ...evalHeaders];
    const rows = studentsData.map(stu => {
      const baseData = [stu.nivel, stu.rut, stu.lastnamepaternal, stu.lastnamematernal, stu.firstname];
      const evalData = isLow
        ? [stu.math_eval1, stu.math_eval2, stu.math_eval3, stu.math_eval4]
        : [stu.math_eval1, stu.math_eval1a, stu.math_eval2, stu.math_eval2a, stu.math_eval3, stu.math_eval3a, stu.math_eval4, stu.math_eval4a];
      return [...baseData, ...evalData];
    });
    return [headers, ...rows];
  };

  // Función para exportar todos los niveles a un archivo Excel con pestañas Y una hoja maestra consolidada
  const exportAllExcel = async () => {
    if (!filterYear) {
      alert("Por favor, seleccione un año para exportar.");
      return;
    }

    setLoading(true);
    const workbook = XLSX.utils.book_new();
    let todosLosEstudiantes = []; // Acumulador para la hoja maestra

    // 1. Obtenemos en paralelo los datos para cada nivel
    const sheetsData = await Promise.all(
      levelOptions.map(async (level) => {
        const dataForLevel = await fetchDataForLevel(level);
        
        // Si hay datos, los sumamos a nuestra lista global
        if (dataForLevel && dataForLevel.length > 0) {
          todosLosEstudiantes = [...todosLosEstudiantes, ...dataForLevel];
        }

        const sheetData = dataForLevel.length > 0
          ? createSheetData(level, dataForLevel)
          : [["No hay datos para este nivel"]];
        return { level, sheetData };
      })
    );

    // 2. CREAR LA HOJA EXTRA (CONSOLIDADO)
    if (todosLosEstudiantes.length > 0) {
      // Ordenamos a todos por apellido paterno
      todosLosEstudiantes.sort((a, b) => a.lastnamepaternal.localeCompare(b.lastnamepaternal));
      
      // Para el consolidado usamos el formato de 8 notas para que quepan todos
      // Usamos un nivel que NO sea 'low' (como 'IVA') para forzar las 8 columnas en el header
      const globalSheetData = createSheetData("IVA", todosLosEstudiantes);
      const globalWorksheet = XLSX.utils.aoa_to_sheet(globalSheetData);
      
      // Añadimos la hoja de "TODOS" al principio del libro
      XLSX.utils.book_append_sheet(workbook, globalWorksheet, "CONSOLIDADO_TOTAL");
    }

    // 3. Se recorren los niveles para agregar cada hoja individual
    levelOptions.forEach(level => {
      const sheetDataObj = sheetsData.find(sheet => sheet.level === level);
      const worksheet = XLSX.utils.aoa_to_sheet(sheetDataObj.sheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, level);
    });
    
    XLSX.writeFile(workbook, `fluidez_matematicas_${filterYear}_completo.xlsx`);
    setLoading(false);
  };
  useEffect(() => {
    fetchStudents();
  }, [filterYear, filterNivel]);

  return (
    <div className="page-content">
      <h2 className="page-title">Fluidez Matemáticas</h2>

      <div className="fluidez-controls">
        <div className="filter">
          <label>Año:</label>
          <select value={filterYear} onChange={e => setFilterYear(e.target.value)}>
            <option value="">Seleccione el año</option>
            <option value="2026">2026</option>
            <option value="2025">2025</option>
          </select>
        </div>
        <div className="filter">
          <label>Nivel:</label>
          <select value={filterNivel} onChange={e => setFilterNivel(e.target.value)}>
            <option value="">Seleccione Nivel</option>
            {levelOptions.map(level => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>
        </div>
        <button className="create-button" onClick={fetchStudents}>Buscar</button>
        <button className="create-button" onClick={exportAllExcel} style={{ marginLeft: '1rem' }}>
          Exportar Todo
        </button>
      </div>

      {loading ? (
        <p>Cargando estudiantes…</p>
      ) : students.length > 0 ? (
        <>
          <div className="table-header">
            <h3>
              {filterNivel
                ? `Lista de Estudiantes del ${filterNivel}`
                : 'Lista de Estudiantes'}
            </h3>
            <button
              className="create-button general-save-button"
              onClick={handleSaveAll}
            >
              Guardar Todo
            </button>
          </div>

          {updateMessage && (
            <p className="message">{updateMessage}</p>
          )}

          <table className="fluidez-table">
            <thead>
              <tr>
                <th>Nivel</th>
                <th>RUT</th>
                <th>Apellido Paterno</th>
                <th>Apellido Materno</th>
                <th>Nombres</th>
                {headerLabels.map(label => (
                  <th key={label}>{label}</th>
                ))}
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {students.map((stu, index) => (
                <tr key={stu.rut}>
                  <td>{stu.nivel}</td>
                  <td>{stu.rut}</td>
                  <td>{stu.lastnamepaternal}</td>
                  <td>{stu.lastnamematernal}</td>
                  <td>{stu.firstname}</td>
                  {fieldsToRender.map(field => (
                    <td key={field}>
                      <input
                        id={`input-${index}-${field}`}
                        type="number"
                        value={stu[field]}
                        onChange={e => handleInputChange(e, index, field)}
                        onKeyDown={e => handleKeyDown(e, index, field, fieldsToRender)}
                        onWheel={e => e.preventDefault()}
                        style={{ width: '40px', fontSize: '0.8rem' }}
                      />
                    </td>
                  ))}
                  <td>
                    <button
                      className="create-button"
                      onClick={() => handleSave(stu)}
                    >
                      Guardar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="csv-actions">
            <button className="create-button" onClick={exportCSV}>Exportar CSV</button>
            <button
              className="create-button"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              style={{ marginLeft: '1rem' }}
            >
              Importar CSV
            </button>
            <input
              type="file"
              accept=".csv"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleImportCSV}
            />
          </div>
        </>
      ) : (
        <p>No hay datos para mostrar.</p>
      )}
    </div>
  );
};

export default FluidezMatematicas;
